/**
 * Koni Vehicle Lookup — Cascading Filter
 * Data source: Shopify product metafields (custom.brand / custom.model /
 *              custom.body_style / custom.year_range) via Search & Discovery.
 *
 * Filter values are fetched from collection filters using Shopify's
 * Section Rendering API.
 *
 * Navigation uses native Shopify storefront filter URLs:
 *   /collections/all?filter.p.m.custom.brand=BMW&filter.p.m.custom.model=1+Series&...
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Metafield filter URL param names (Shopify storefront filter format)
  // ---------------------------------------------------------------------------
  var F_BRAND     = 'filter.p.m.custom.brand';
  var F_MODEL     = 'filter.p.m.custom.model';
  var F_BODYSTYLE = 'filter.p.m.custom.body_style';
  var F_YEAR      = 'filter.p.m.custom.year_range';
  var F_ENGINE    = 'filter.p.m.custom.engines';

  var SESSION_KEY = '__koniVehicle';
  var vehicleDataRecordsPromise = null;
  var BRAND_COLLECTION_HANDLE_BY_INITIAL = {
    A: 'vehicle-finder-a-brands',
    B: 'vehicle-finder-b-c-brands',
    C: 'vehicle-finder-b-c-brands',
    D: 'vehicle-finder-d-l-brands',
    E: 'vehicle-finder-d-l-brands',
    F: 'vehicle-finder-d-l-brands',
    G: 'vehicle-finder-d-l-brands',
    H: 'vehicle-finder-d-l-brands',
    I: 'vehicle-finder-d-l-brands',
    J: 'vehicle-finder-d-l-brands',
    K: 'vehicle-finder-d-l-brands',
    L: 'vehicle-finder-d-l-brands',
    M: 'vehicle-finder-m-brands',
    N: 'vehicle-finder-n-r-brands',
    O: 'vehicle-finder-n-r-brands',
    P: 'vehicle-finder-n-r-brands',
    Q: 'vehicle-finder-n-r-brands',
    R: 'vehicle-finder-n-r-brands',
    S: 'vehicle-finder-s-brands',
    T: 'vehicle-finder-t-brands',
    V: 'vehicle-finder-v-w-brands',
    W: 'vehicle-finder-v-w-brands'
  };

  // ---------------------------------------------------------------------------
  // Filter value helpers
  // ---------------------------------------------------------------------------

  /**
   * Extracts the sorted, non-empty label list for a given filter param from
   * a Shopify collection.filters JSON array.
   */
  function getFilterValues(filters, paramName) {
    if (!Array.isArray(filters)) return [];
    var group = null;
    for (var i = 0; i < filters.length; i++) {
      if (filters[i].param_name === paramName || filters[i].id === paramName) {
        group = filters[i];
        break;
      }
    }
    if (!group || !Array.isArray(group.values)) return [];
    return group.values
      .filter(function (v) { return v && (v.count === undefined || v.count > 0); })
      .map(function (v) { return v.label; })
      .sort(function (a, b) { return a.localeCompare(b, undefined, { sensitivity: 'base' }); });
  }

  function normalizeText(val) {
    return (typeof val === 'string' ? val : '').trim();
  }

  function sortedUnique(values) {
    var map = {};
    values.forEach(function (v) {
      var t = normalizeText(v);
      if (t) map[t] = true;
    });
    return Object.keys(map).sort(function (a, b) {
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
  }

  function normalizeEngineList(val) {
    if (Array.isArray(val)) {
      return val.map(normalizeText).filter(Boolean);
    }
    var raw = normalizeText(val);
    if (!raw) return [];
    return raw.split(/[\n,]+/g).map(normalizeText).filter(Boolean);
  }

  function normalizeVehicleRecords(raw) {
    if (!Array.isArray(raw)) return [];
    return raw.map(function (row) {
      if (!row || typeof row !== 'object') return null;
      var brand = normalizeText(row.brand || row.Brand);
      var model = normalizeText(row.model || row.Model);
      var bodyStyle = normalizeText(row.body_style || row.bodyStyle || row.listing || row.Listing);
      var year = normalizeText(row.year_range || row.yearRange || row.year || row.Year);
      var engines = normalizeEngineList(row.engine || row.engines || row.Engine || row.Engines);
      if (!brand) return null;
      return { brand: brand, model: model, bodyStyle: bodyStyle, year: year, engines: engines };
    }).filter(Boolean);
  }

  async function loadVehicleDataRecords() {
    var dataUrl = window.__koniVehicleDataUrl;
    if (!dataUrl) return null;
    if (vehicleDataRecordsPromise) return vehicleDataRecordsPromise;

    vehicleDataRecordsPromise = fetch(dataUrl, { credentials: 'same-origin' })
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (payload) {
        var rows = Array.isArray(payload) ? payload : (payload && payload.records) || [];
        var normalized = normalizeVehicleRecords(rows);
        if (!normalized.length) {
          throw new Error('empty vehicle JSON records');
        }
        return normalized;
      })
      .catch(function (e) {
        console.warn('[Koni] vehicle_data_url load failed, falling back to filter source:', e);
        return null;
      });

    return vehicleDataRecordsPromise;
  }

  function recordMatches(record, brand, model, bodyStyle, year) {
    if (brand && record.brand !== brand) return false;
    if (model && record.model !== model) return false;
    if (bodyStyle && record.bodyStyle !== bodyStyle) return false;
    if (year && record.year !== year) return false;
    return true;
  }

  function getRecordOptions(records, brand, model, bodyStyle, year) {
    var brands = sortedUnique(records.map(function (r) { return r.brand; }));
    var models = sortedUnique(records
      .filter(function (r) { return recordMatches(r, brand, '', '', ''); })
      .map(function (r) { return r.model; }));
    var bodyStyles = sortedUnique(records
      .filter(function (r) { return recordMatches(r, brand, model, '', ''); })
      .map(function (r) { return r.bodyStyle; }));
    var years = sortedUnique(records
      .filter(function (r) { return recordMatches(r, brand, model, bodyStyle, ''); })
      .map(function (r) { return r.year; }));
    var engines = sortedUnique(
      records
        .filter(function (r) { return recordMatches(r, brand, model, bodyStyle, year || ''); })
        .reduce(function (acc, r) {
          if (Array.isArray(r.engines) && r.engines.length) acc.push.apply(acc, r.engines);
          return acc;
        }, [])
    );

    return {
      brands: brands,
      models: models,
      bodyStyles: bodyStyles,
      years: years,
      engines: engines
    };
  }

  /**
   * Fetches updated collection.filters data via Shopify's Section Rendering API.
   * Applies brand/model/bodyStyle as active filters so the returned values
   * are scoped — e.g. passing brand='BMW' returns only BMW models.
   */
  async function fetchCascadeFilters(brand, model, bodyStyle, year) {
    var handle = window.__koniFilterSourceHandle || window.__koniCollectionHandle || 'all';
    var p = new URLSearchParams();
    if (brand)     p.set(F_BRAND,     brand);
    if (model)     p.set(F_MODEL,     model);
    if (bodyStyle) p.set(F_BODYSTYLE, bodyStyle);
    if (year)      p.set(F_YEAR,      year);

    function normalizeFilters(raw) {
      if (!Array.isArray(raw)) return null;
      var hasParamNames = raw.some(function (f) {
        return f && (typeof f.param_name === 'string' || typeof f.id === 'string');
      });
      return hasParamNames ? raw : null;
    }

    function extractFiltersFromHtml(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');

      // Preferred path when Shopify returns a valid filters JSON payload.
      var el = doc.getElementById('koni-filter-data');
      if (el) {
        try {
          var parsed = JSON.parse(el.textContent);
          var normalized = normalizeFilters(parsed);
          if (normalized) return normalized;
        } catch (e) {}
      }

      // Fallback: parse storefront filter checkboxes directly from markup.
      var keys = [F_BRAND, F_MODEL, F_BODYSTYLE, F_YEAR, F_ENGINE];
      var map = {};
      keys.forEach(function (k) { map[k] = []; });

      keys.forEach(function (k) {
        var inputs = doc.querySelectorAll('input[type="checkbox"][name="' + k + '"]');
        var seen = {};
        inputs.forEach(function (input) {
          var val = (input.value || '').trim();
          if (!val || seen[val]) return;
          seen[val] = true;
          map[k].push({ label: val, count: 1 });
        });
      });

      return keys.map(function (k) {
        return { param_name: k, values: map[k] };
      });
    }

    async function fetchFiltersAt(url) {
      var resp = await fetch(url, { credentials: 'same-origin' });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      var html = await resp.text();
      return extractFiltersFromHtml(html);
    }

    var basePath = '/collections/' + handle;
    var query = p.toString();
    var attempts = [
      basePath + '?' + query + (query ? '&' : '') + 'section_id=main',
      basePath + '?' + query + (query ? '&' : '') + 'section_id=main-collection-product-grid',
      basePath + (query ? '?' + query : '')
    ];

    for (var i = 0; i < attempts.length; i++) {
      try {
        return await fetchFiltersAt(attempts[i]);
      } catch (e) {
        if (i === attempts.length - 1) {
          console.warn('[Koni] fetchCascadeFilters failed:', e);
        }
      }
    }
    return null;
  }

  /**
   * Returns the initial filter data for the widget.
   */
  async function getInitialFilters() {
    return fetchCascadeFilters(null, null, null, null);
  }

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  function populateSelect(sel, options, placeholder, selectedVal) {
    var prev = sel.value;
    sel.innerHTML = '<option value="">' + placeholder + '</option>';
    options.forEach(function (opt) {
      var o = document.createElement('option');
      o.value = opt;
      o.textContent = opt;
      if (opt === (selectedVal !== undefined ? selectedVal : prev)) o.selected = true;
      sel.appendChild(o);
    });
    sel.disabled = options.length === 0;
  }

  function clearSelect(sel, placeholder) {
    sel.innerHTML = '<option value="">' + placeholder + '</option>';
    sel.disabled = true;
  }

  // ---------------------------------------------------------------------------
  // URL helpers
  // ---------------------------------------------------------------------------

  /**
   * Strips Shopify tag-path segments from a collection URL, leaving only
   * the /collections/{handle} portion.
   */
  function getBaseCollectionPath(pathname) {
    var match = pathname.match(/^(\/collections\/[^\/]+)/);
    return match ? match[1] : pathname;
  }

  /**
   * Builds a native Shopify storefront metafield filter URL.
   * e.g. /collections/all?filter.p.m.custom.brand=BMW&filter.p.m.custom.model=1+Series
   */
  function buildMetafieldUrl(brand, model, bodyStyle, year, engine, collectionPath) {
    var base = getBaseCollectionPath(collectionPath || window.location.pathname);
    var p = new URLSearchParams(window.location.search);
    [F_BRAND, F_MODEL, F_BODYSTYLE, F_YEAR, F_ENGINE].forEach(function (k) { p.delete(k); });
    p.delete('page');
    if (brand)     p.set(F_BRAND,     brand);
    if (model)     p.set(F_MODEL,     model);
    if (bodyStyle) p.set(F_BODYSTYLE, bodyStyle);
    if (year)      p.set(F_YEAR,      year);
    if (engine)    p.set(F_ENGINE,    engine);
    var qs = p.toString();
    return base + (qs ? '?' + qs : '');
  }

  function resolveCollectionPathForBrand(brand) {
    var normalizedBrand = normalizeText(brand);
    if (!normalizedBrand) return '/collections/all';
    var initialMatch = normalizedBrand.match(/[A-Za-z]/);
    if (!initialMatch) return '/collections/all';
    var initial = initialMatch[0].toUpperCase();
    var handle = BRAND_COLLECTION_HANDLE_BY_INITIAL[initial];
    return handle ? '/collections/' + handle : '/collections/all';
  }

  // ---------------------------------------------------------------------------
  // sessionStorage — vehicle selection persisted across page navigations
  // ---------------------------------------------------------------------------

  function saveVehicleSession(brand, model, bodyStyle, year, engine) {
    try {
      if (brand) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({
          brand: brand, model: model, bodyStyle: bodyStyle, year: year, engine: engine
        }));
      } else {
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch (e) {}
  }

  function loadVehicleSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function clearVehicleSession() {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Homepage widget
  // ---------------------------------------------------------------------------

  async function initWidget(container) {
    var selBrand   = container.querySelector('[data-vl-brand]');
    var selModel   = container.querySelector('[data-vl-model]');
    var selListing = container.querySelector('[data-vl-listing]');
    var selYear    = container.querySelector('[data-vl-year]');
    var selEngine  = container.querySelector('[data-vl-engine]');
    var btnSearch  = container.querySelector('[data-vl-search]');
    var btnReset   = container.querySelector('[data-vl-reset]');

    if (!selBrand) return;

    function updateSearchBtn() {
      if (btnSearch) btnSearch.disabled = !selBrand.value;
    }

    var records = await loadVehicleDataRecords();
    if (records && records.length) {
      async function getEngineOptionsWithFallback(brand, model, bodyStyle, year) {
        var fromJson = getRecordOptions(records, brand, model, bodyStyle, year).engines;
        if (fromJson.length) return fromJson;
        var filters = await fetchCascadeFilters(brand, model, bodyStyle, year);
        return getFilterValues(filters, F_ENGINE);
      }

      var savedFromJson = loadVehicleSession();
      var root = getRecordOptions(records, '', '', '', '');
      populateSelect(selBrand, root.brands, 'Select Brand', savedFromJson ? savedFromJson.brand : '');
      selBrand.disabled = false;

      if (savedFromJson && savedFromJson.brand) {
        var byBrand = getRecordOptions(records, savedFromJson.brand, '', '', '');
        populateSelect(selModel, byBrand.models, 'Select Model', savedFromJson.model);
        if (savedFromJson.model) {
          var byModel = getRecordOptions(records, savedFromJson.brand, savedFromJson.model, '', '');
          populateSelect(selListing, byModel.bodyStyles, 'Select Listing', savedFromJson.bodyStyle);
          if (savedFromJson.bodyStyle) {
            var byBody = getRecordOptions(records, savedFromJson.brand, savedFromJson.model, savedFromJson.bodyStyle, '');
            populateSelect(selYear, byBody.years, 'Year Range', savedFromJson.year);
            if (savedFromJson.year && selEngine) {
              var savedEngines = await getEngineOptionsWithFallback(
                savedFromJson.brand,
                savedFromJson.model,
                savedFromJson.bodyStyle,
                savedFromJson.year
              );
              populateSelect(selEngine, savedEngines, 'Engine', savedFromJson.engine);
            } else if (selEngine) {
              clearSelect(selEngine, 'Engine');
            }
          } else {
            clearSelect(selYear, 'Year Range');
            if (selEngine) clearSelect(selEngine, 'Engine');
          }
        } else {
          clearSelect(selListing, 'Select Listing');
          clearSelect(selYear, 'Year Range');
          if (selEngine) clearSelect(selEngine, 'Engine');
        }
      } else {
        clearSelect(selModel, 'Select Model');
        clearSelect(selListing, 'Select Listing');
        clearSelect(selYear, 'Year Range');
        if (selEngine) clearSelect(selEngine, 'Engine');
      }

      updateSearchBtn();

      selBrand.addEventListener('change', function () {
        clearSelect(selModel, 'Select Model');
        clearSelect(selListing, 'Select Listing');
        clearSelect(selYear, 'Year Range');
        if (selEngine) clearSelect(selEngine, 'Engine');
        updateSearchBtn();
        if (!selBrand.value) return;
        var options = getRecordOptions(records, selBrand.value, '', '', '');
        populateSelect(selModel, options.models, 'Select Model');
      });

      selModel.addEventListener('change', function () {
        clearSelect(selListing, 'Select Listing');
        clearSelect(selYear, 'Year Range');
        if (selEngine) clearSelect(selEngine, 'Engine');
        updateSearchBtn();
        if (!selModel.value) return;
        var options = getRecordOptions(records, selBrand.value, selModel.value, '', '');
        populateSelect(selListing, options.bodyStyles, 'Select Listing');
      });

      selListing.addEventListener('change', function () {
        clearSelect(selYear, 'Year Range');
        if (selEngine) clearSelect(selEngine, 'Engine');
        updateSearchBtn();
        if (!selListing.value) return;
        var options = getRecordOptions(records, selBrand.value, selModel.value, selListing.value, '');
        populateSelect(selYear, options.years, 'Year Range');
      });

      selYear.addEventListener('change', async function () {
        if (selEngine) {
          clearSelect(selEngine, 'Engine');
          if (selYear.value) {
            var engineOptions = await getEngineOptionsWithFallback(
              selBrand.value,
              selModel.value,
              selListing.value,
              selYear.value
            );
            populateSelect(selEngine, engineOptions, 'Engine');
          }
        }
        updateSearchBtn();
      });

      if (selEngine) selEngine.addEventListener('change', updateSearchBtn);

      if (btnSearch) {
        btnSearch.addEventListener('click', function (e) {
          e.preventDefault();
          if (!selBrand.value) return;
          saveVehicleSession(selBrand.value, selModel.value, selListing.value, selYear.value, selEngine ? selEngine.value : '');
          var destinationCollectionPath = resolveCollectionPathForBrand(selBrand.value);
          window.location.href = buildMetafieldUrl(
            selBrand.value,
            selModel.value,
            selListing.value,
            selYear.value,
            selEngine ? selEngine.value : '',
            destinationCollectionPath
          );
        });
      }

      if (btnReset) {
        btnReset.addEventListener('click', function (e) {
          e.preventDefault();
          clearVehicleSession();
          var fresh = getRecordOptions(records, '', '', '', '');
          populateSelect(selBrand, fresh.brands, 'Select Brand');
          clearSelect(selModel, 'Select Model');
          clearSelect(selListing, 'Select Listing');
          clearSelect(selYear, 'Year Range');
          if (selEngine) clearSelect(selEngine, 'Engine');
          updateSearchBtn();
        });
      }

      return;
    }

    // __koniCollectionHandle is set by vehicle-lookup.liquid to the destination
    // collection handle so fetchCascadeFilters hits the right collection.
    var initialFilters = await getInitialFilters();
    var brands = getFilterValues(initialFilters, F_BRAND);

    var saved = loadVehicleSession();
    populateSelect(selBrand, brands, 'Select Brand', saved ? saved.brand : '');
    selBrand.disabled = false; // brand is always interactive regardless of available options

    // Pre-populate cascade levels from sessionStorage
    if (saved && saved.brand) {
      var f1 = await fetchCascadeFilters(saved.brand, null, null, null);
      if (f1) {
        populateSelect(selModel, getFilterValues(f1, F_MODEL), 'Select Model', saved.model);
        if (saved.model) {
          var f2 = await fetchCascadeFilters(saved.brand, saved.model, null, null);
          if (f2) {
            populateSelect(selListing, getFilterValues(f2, F_BODYSTYLE), 'Select Listing', saved.bodyStyle);
            if (saved.bodyStyle) {
              var f3 = await fetchCascadeFilters(saved.brand, saved.model, saved.bodyStyle, null);
              if (f3) {
                populateSelect(selYear, getFilterValues(f3, F_YEAR), 'Year Range', saved.year);
                if (saved.year && selEngine) {
                  var f4 = await fetchCascadeFilters(saved.brand, saved.model, saved.bodyStyle, saved.year);
                  if (f4) populateSelect(selEngine, getFilterValues(f4, F_ENGINE), 'Engine', saved.engine);
                } else if (selEngine) {
                  clearSelect(selEngine, 'Engine');
                }
              }
            }
          }
        }
      }
    } else {
      clearSelect(selModel,   'Select Model');
      clearSelect(selListing, 'Select Listing');
      clearSelect(selYear,    'Year Range');
      if (selEngine) clearSelect(selEngine, 'Engine');
    }

    updateSearchBtn();

    // Cascade: Brand → Model
    selBrand.addEventListener('change', function () {
      clearSelect(selModel,   'Select Model');
      clearSelect(selListing, 'Select Listing');
      clearSelect(selYear,    'Year Range');
      if (selEngine) clearSelect(selEngine, 'Engine');
      updateSearchBtn();
      if (!selBrand.value) return;
      fetchCascadeFilters(selBrand.value).then(function (f) {
        if (f) populateSelect(selModel, getFilterValues(f, F_MODEL), 'Select Model');
        updateSearchBtn();
      });
    });

    // Cascade: Model → Listing (custom.body_style)
    selModel.addEventListener('change', function () {
      clearSelect(selListing, 'Select Listing');
      clearSelect(selYear,    'Year Range');
      if (selEngine) clearSelect(selEngine, 'Engine');
      updateSearchBtn();
      if (!selModel.value) return;
      fetchCascadeFilters(selBrand.value, selModel.value).then(function (f) {
        if (f) populateSelect(selListing, getFilterValues(f, F_BODYSTYLE), 'Select Listing');
        updateSearchBtn();
      });
    });

    // Cascade: Listing → Year Range
    selListing.addEventListener('change', function () {
      clearSelect(selYear, 'Year Range');
      if (selEngine) clearSelect(selEngine, 'Engine');
      updateSearchBtn();
      if (!selListing.value) return;
      fetchCascadeFilters(selBrand.value, selModel.value, selListing.value).then(function (f) {
        if (f) populateSelect(selYear, getFilterValues(f, F_YEAR), 'Year Range');
        updateSearchBtn();
      });
    });

    // Cascade: Year Range → Engine
    selYear.addEventListener('change', function () {
      if (selEngine) {
        clearSelect(selEngine, 'Engine');
        if (selYear.value) {
          fetchCascadeFilters(selBrand.value, selModel.value, selListing.value, selYear.value).then(function (f) {
            if (f) populateSelect(selEngine, getFilterValues(f, F_ENGINE), 'Engine');
            updateSearchBtn();
          });
          return;
        }
      }
      updateSearchBtn();
    });

    if (selEngine) selEngine.addEventListener('change', updateSearchBtn);

    // Search
    if (btnSearch) {
      btnSearch.addEventListener('click', function (e) {
        e.preventDefault();
        if (!selBrand.value) return;
        saveVehicleSession(selBrand.value, selModel.value, selListing.value, selYear.value, selEngine ? selEngine.value : '');
        var destinationCollectionPath = resolveCollectionPathForBrand(selBrand.value);
        window.location.href = buildMetafieldUrl(
          selBrand.value,
          selModel.value,
          selListing.value,
          selYear.value,
          selEngine ? selEngine.value : '',
          destinationCollectionPath
        );
      });
    }

    // Reset
    if (btnReset) {
      btnReset.addEventListener('click', async function (e) {
        e.preventDefault();
        clearVehicleSession();
        // Clear dropdowns in-place and reload brand list
        var freshFilters = await getInitialFilters();
        populateSelect(selBrand, getFilterValues(freshFilters, F_BRAND), 'Select Brand');
        clearSelect(selModel,   'Select Model');
        clearSelect(selListing, 'Select Listing');
        clearSelect(selYear,    'Year Range');
        if (selEngine) clearSelect(selEngine, 'Engine');
        updateSearchBtn();
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function boot() {
    document.querySelectorAll('[data-vehicle-lookup]').forEach(function (container) {
      initWidget(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
